import { useEffect, useState } from "react";
import { Play, Square, Plus, Download, Trash2, X, CheckCircle, Clock } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-CA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}
function hours(a, b) { if (!a || !b) return 0; return Math.max((new Date(b) - new Date(a)) / 3600000, 0); }
function weekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay() === 0 ? 6 : now.getDay() - 1; // monday-based
  const mon = new Date(now); mon.setDate(now.getDate() - day + offset * 7); mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23, 59, 59, 999);
  return { start: mon.toISOString(), end: sun.toISOString(), monStart: mon };
}

export default function Timesheets() {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const range = weekRange(weekOffset);

  const load = async () => {
    const [{ data: es }, { data: ee }, { data: jj }, { data: a }] = await Promise.all([
      api.get(`/time-entries?start=${range.start}&end=${range.end}`),
      api.get("/employees"),
      api.get("/jobs?include_closed=true"),
      api.get("/clock/active"),
    ]);
    setEntries(es); setEmployees(ee); setJobs(jj);
    setActive(a.active === false ? null : a);
  };
  useEffect(() => { load(); }, [weekOffset]);

  const clockIn = async (jobId) => {
    try {
      await api.post("/clock/in", { job_id: jobId || null, billable: true });
      await load();
    } catch (e) { alert(formatErr(e)); }
  };
  const clockOut = async () => {
    try { await api.post("/clock/out"); await load(); } catch (e) { alert(formatErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/time-entries/${id}`); await load();
  };
  const approve = async (id, val) => { await api.post(`/time-entries/${id}/approve?approved=${val}`); await load(); };

  const blank = { employee_id: "", job_id: "", start_at: "", end_at: "", notes: "", billable: true, hourly_rate: 0, approved: false };
  const openNew = () => { setEditing({ ...blank, start_at: new Date().toISOString().slice(0, 16), end_at: new Date().toISOString().slice(0, 16) }); setShowForm(true); };
  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {
        ...editing,
        start_at: editing.start_at ? new Date(editing.start_at).toISOString() : null,
        end_at: editing.end_at ? new Date(editing.end_at).toISOString() : null,
        hourly_rate: Number(editing.hourly_rate || 0),
      };
      if (editing.id) await api.put(`/time-entries/${editing.id}`, payload);
      else await api.post("/time-entries", payload);
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const totalHours = entries.reduce((s, t) => s + hours(t.start_at, t.end_at), 0);
  const billableHours = entries.filter((t) => t.billable).reduce((s, t) => s + hours(t.start_at, t.end_at), 0);

  return (
    <div data-testid="timesheets-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Time Tracking</h1><p className="text-sm text-slate-500 mt-1">Clock in/out and weekly timesheets</p></div>
        <div className="flex gap-2 flex-wrap">
          <a href={fileUrl(`/time-entries/export?start=${range.start}&end=${range.end}`)} data-testid="export-timesheets-btn" className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2"><Download size={14} /> Export CSV</a>
          <button onClick={openNew} data-testid="add-time-entry-btn" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> Manual Entry</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display font-semibold flex items-center gap-2"><Clock size={18} /> Active Timer</h3>
          </div>
          {active ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded p-4">
              <div>
                <div className="text-sm">Clocked in at <span className="font-medium tabular">{fmtDateTime(active.start_at)}</span></div>
                {active.job_id && <div className="text-xs text-slate-500">Job: {jobs.find((j) => j.id === active.job_id)?.number || "—"}</div>}
                <div className="text-xs text-slate-500">Elapsed: <Elapsed start={active.start_at} /></div>
              </div>
              <button onClick={clockOut} data-testid="stop-timer-btn" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md font-medium flex items-center gap-2"><Square size={14} /> Clock Out</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={() => clockIn(null)} data-testid="start-timer-btn" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md font-medium flex items-center gap-2"><Play size={14} /> Start Timer</button>
              <span className="text-xs text-slate-500">or open a job and clock in there</span>
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display font-semibold mb-3">Week Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total hours</span><span className="tabular font-semibold">{totalHours.toFixed(2)}h</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Billable</span><span className="tabular font-semibold text-emerald-700">{billableHours.toFixed(2)}h</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Non-billable</span><span className="tabular">{(totalHours - billableHours).toFixed(2)}h</span></div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => setWeekOffset(weekOffset - 1)} className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50">‹ Prev</button>
            <h3 className="font-display font-semibold">Week of {range.monStart.toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" })}</h3>
            <button onClick={() => setWeekOffset(weekOffset + 1)} className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50">Next ›</button>
            {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="px-2 py-1 text-xs text-[#0F4C81] hover:underline">Today</button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Employee</th>
                <th className="px-4 py-2 text-left">Job</th>
                <th className="px-4 py-2 text-left">Start</th>
                <th className="px-4 py-2 text-left">End</th>
                <th className="px-4 py-2 text-right">Hours</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-left">Billable</th>
                <th className="px-4 py-2 text-left">Approved</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No time entries this week.</td></tr>}
              {entries.map((t) => {
                const h = hours(t.start_at, t.end_at);
                const amt = t.billable ? h * Number(t.hourly_rate || 0) : 0;
                return (
                  <tr key={t.id} data-testid={`time-row-${t.id}`} className="border-t border-slate-100">
                    <td className="px-4 py-2">{employees.find((e) => e.id === t.employee_id)?.name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-slate-600">{jobs.find((j) => j.id === t.job_id)?.number || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-slate-600">{fmtDateTime(t.start_at)}</td>
                    <td className="px-4 py-2 text-slate-600">{t.end_at ? fmtDateTime(t.end_at) : <span className="text-amber-600">Active</span>}</td>
                    <td className="px-4 py-2 text-right tabular">{h.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtCAD(amt)}</td>
                    <td className="px-4 py-2">{t.billable ? "Yes" : "No"}</td>
                    <td className="px-4 py-2">
                      <button onClick={() => approve(t.id, !t.approved)} className={`text-xs px-2 py-0.5 rounded ${t.approved ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{t.approved ? "Approved" : "Pending"}</button>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => { setEditing({ ...t, start_at: t.start_at?.slice(0, 16), end_at: t.end_at?.slice(0, 16) }); setShowForm(true); }} className="text-xs text-[#0F4C81] hover:underline mr-2">Edit</button>
                      <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-md max-w-lg w-full p-6 space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">{editing.id ? "Edit" : "Manual"} Time Entry</h3><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Employee</label>
                <select value={editing.employee_id || ""} onChange={(e) => setEditing({ ...editing, employee_id: e.target.value, hourly_rate: employees.find((x) => x.id === e.target.value)?.hourly_rate || editing.hourly_rate })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  <option value="">— Self —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Job</label>
                <select value={editing.job_id || ""} onChange={(e) => setEditing({ ...editing, job_id: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  <option value="">— None —</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.number} {j.job_type ? "· " + j.job_type : ""}</option>)}
                </select>
              </div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Start *</label><input type="datetime-local" value={editing.start_at} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">End</label><input type="datetime-local" value={editing.end_at || ""} onChange={(e) => setEditing({ ...editing, end_at: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Hourly Rate ($)</label><input type="number" step="0.01" value={editing.hourly_rate} onChange={(e) => setEditing({ ...editing, hourly_rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular" /></div>
              <div className="flex items-end gap-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.billable} onChange={(e) => setEditing({ ...editing, billable: e.target.checked })} /> Billable</label></div>
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Notes</label><textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Elapsed({ start }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = now - new Date(start).getTime();
  const s = Math.max(Math.floor(ms / 1000), 0);
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  return <span className="font-mono tabular">{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(sec).padStart(2, "0")}</span>;
}
